'use client'
import { Icon } from '@/components'
import { LoddingIcon } from '@/components/icon-svg'
import { getBlogPostsByCategory } from '@/features/blogs'
import { useDebounce } from '@/hooks'
import { Autocomplete, Box, IconButton, InputAdornment, InputBase, Popper, Typography } from '@mui/material'
import { ClearIcon } from '@mui/x-date-pickers/icons'
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from '@/features/ref/components/Link'
import { useState } from 'react'

export const SearchBlog = () => {
  const [value, setValue] = useState('')
  const [open, setOpen] = useState(false)
  const [listBlogs, setListBlogs] = useState<IBlogPost[]>([])
  const debounceValue = useDebounce(value, 300)
  const t = useTranslations()

  const { mutate: getBlogs, isPending } = useMutation({
    mutationKey: ['LIST-BLOGS', debounceValue],
    mutationFn: async (valueSearch: string) => {
      return await getBlogPostsByCategory({
        action: 'get_post_list',
        categoryId: 6,
        pageSize: 5,
        filter: {
          name: {
            like: `%${valueSearch}%`,
          },
        },
      })
    },
    onSuccess: (data) => {
      const newData = data?.data?.items || []
      setListBlogs(newData)
    },
  })

  const handleSearch = () => {
    getBlogs(debounceValue as string)
    setOpen(true)
  }

  const searchWithoutDebounce = () => {
    getBlogs(value)
    setOpen(true)
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Autocomplete
        freeSolo
        onInputChange={(_, newValue) => setValue(newValue)}
        options={listBlogs}
        filterOptions={(x) => x}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
        loading={isPending}
        open={open}
        blurOnSelect
        loadingText={
          <div className="my-5 flex items-center justify-center">
            {isPending ? <LoddingIcon /> : t('not-found')}
          </div>
        }
        clearIcon={null}
        noOptionsText={
          <div className="my-5 flex items-center justify-center">
            {isPending ? <LoddingIcon /> : t('not-found')}
          </div>
        }
        renderInput={(params) => (
          <InputBase
            {...params.InputProps}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Tìm kiếm mặt hàng',
              value: value,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value),
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.code === 'Enter') {
                  searchWithoutDebounce()
                  // if (highlightedOption) {
                  //   router.push(`/blogs/${highlightedOption.url_key}`)
                  // } else {
                  //   handleSearch()
                  // }
                }
              },
              onFocus: () => {
                setOpen(true)
              },
              onBlur: () => {
                setOpen(false)
              },
            }}
            placeholder={t('Tìm kiếm bài viết')}
            sx={{
              maxWidth: '280px',
              transform: 'all 0.3s ease-in-out',
              flex: 1,
              background: (theme) => theme.palette.secondary.light,
              color: 'white',
              p: '6px 12px!important',
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: 1.5,
              borderRadius: '4px',
              width: '100%',
              marginLeft: 'auto',
              display: 'flex',
            }}
            endAdornment={
              <InputAdornment position="end">
                {value && (
                  <IconButton size="small" onClick={() => setValue('')} edge="end">
                    <ClearIcon fontSize="small" sx={{ color: 'white' }} />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => handleSearch()} edge="end">
                  <Icon.Search fontSize="small" sx={{ color: 'white' }} />
                </IconButton>
              </InputAdornment>
            }
          />
        )}
        PopperComponent={CustomPopper}
        renderOption={(props, option) => (
          <li {...props} key={option?.post_id}>
            <Link
              href={`/blog/${option?.url_key}`}
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Image alt={option?.name || 'Ảnh bài viết'} src={option?.image} width={60} height={40} />
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    lineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {option.name}
                </Typography>
              </Box>
            </Link>
          </li>
        )}
      />
    </Box>
  )
}
const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      sx={{
        width: { sm: '100%!important', md: '500px!important' },
      }}
    />
  )
}
